import { create } from "zustand";
import { FetcherState } from "../types/fetcher";
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { useAuthStore } from "./auth";
import { navigate } from "gatsby";
import { useAppStore } from "./app";
import { useProfileStore } from "./profile";

let isRefreshing = false;
let failedQueue: {
  resolve: (value: unknown) => void;
  reject: (reason?: any) => void;
}[] = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const createFetch = (workspace?: string): AxiosInstance => {
  const baseURL = workspace
    ? `${process.env.GATSBY_OMS_API_URL}/app/ws/${workspace}`
    : `${process.env.GATSBY_OMS_API_URL}/app`;
  const fetcher = axios.create({ baseURL });

  fetcher.interceptors.request.use(
    (request: InternalAxiosRequestConfig) => {
      const token = useAuthStore.getState().token();
      if (token) {
        request.headers["Authorization"] = `Bearer ${token}`;
      }
      return request;
    },
    (error: any) => {
      console.log(`Error: Failed to send a request ${error}`);
      return Promise.reject(error);
    }
  );

  fetcher.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error: unknown) => {
      if (!axios.isAxiosError(error)) {
        if (error instanceof Error) {
          console.log(`Non-Axios error: ${error.message}`);
        } else {
          console.log("Non-Axios error: Unknown network error");
        }
        return Promise.reject(error);
      }

      const axiosError = error as AxiosError;
      const originalRequest = axiosError.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };

      if (axiosError.response?.status === 401 && !originalRequest?._retry) {
        if (isRefreshing) {
          return new Promise(function (resolve, reject) {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              if (originalRequest.headers) {
                originalRequest.headers["Authorization"] = "Bearer " + token;
                originalRequest._retry = true;
              }
              return fetcher(originalRequest);
            })
            .catch(err => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        return new Promise((resolve, reject) => {
          useAuthStore
            .getState()
            .fetchSession()
            .then(() => {
              const newToken = useAuthStore.getState().token();
              if (originalRequest.headers) {
                originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              }
              processQueue(null, newToken);
              resolve(fetcher(originalRequest));
            })
            .catch(err => {
              processQueue(err, null);
              useAuthStore.getState().signOut();
              reject(err);
            })
            .finally(() => {
              isRefreshing = false;
            });
        });
      }

      if (axiosError.response) {
        if (axiosError.response.status === 503) {
          // Redirect to maintenance page
          useAppStore.getState().setMaintenanceRedirect(true);
        } else {
          console.log(
            `Error: Response (${axiosError.response.status}) ${axiosError.message}`
          );
        }
      } else {
        const errorCode = axiosError.code;
        const errorMessage = axiosError.message
        const isCanceled =
          axios.isCancel(axiosError) || errorCode === AxiosError.ERR_CANCELED;
        if (isCanceled) {
          return Promise.reject(axiosError);
        }
        const isOffline =
          typeof navigator !== "undefined" && navigator.onLine === false;
        if (isOffline) {
          navigate("/network-error");
        } else {
          console.log(`Network error: ${errorMessage}`);
        }
      }

      return Promise.reject(axiosError);
    }
  );
  return fetcher;
};

export const useFetcher = create<FetcherState>(
  (set, get) =>
    ({
      omsFetcher: createFetch(),
      omsDsFetcher: createFetch("global"),
      updateDsFetcher: (): AxiosInstance => {
        const domain = useProfileStore.getState().currentDomain;
        const omsDsFetcher = domain?.id
          ? createFetch(domain.id)
          : createFetch("global");
        set(() => ({ omsDsFetcher }));
        return omsDsFetcher;
      },
    } as FetcherState)
);
